import merge from 'lodash/merge'
import cloneDeep from 'lodash/cloneDeep'

import {
  numberToArea,
  numberToCurrency,
  numberToCurrencyPerArea,
  numberToDays,
  numberToHuman,
  numberToPercentage
} from '../../javascript/helpers/number_helper'
import { BaseChartController, colors, defaultBoldTextStyle, tooltipValueFormatter } from './base_chart_component_controller'

export class XYChartController extends BaseChartController {
  static values = merge(cloneDeep(BaseChartController.values), {
    axisLabel: String,
    xAxisShow: { type: Boolean, default: true },
    xAxisType: String,
    xAxisName: String,
    xAxisStartZero: { type: Boolean, default: false },
    yAxisShow: { type: Boolean, default: true },
    yAxisType: String,
    yAxisName: String,
    yAxisStartZero: { type: Boolean, default: false },
    tooltipAxisType: String,
    tooltipValueFormatter: { type: String, default: 'yAxis' },
    numberOfForecasts: { type: Number, default: 0 },
    markLine: Object
  })

  defaultSeriesStyle = {
    symbol: 'circle',
    symbolSize: 8,
    showSymbol: false,
    lineStyle: {
      width: 3
    }
  }

  transformToDataset (series) {
    return series.map((s) => {
      return {
        dimension: ['x', 'y'],
        source: s.data
      }
    })
  }

  transformToSeries (series, options, offset = 0) {
    return series.map((s, index) => {
      return merge(cloneDeep(this.defaultSeriesStyle), cloneDeep(options), {
        name: s.title,
        label: s.label,
        colorBy: s.colorBy,
        silent: s.silent,
        stack: s.stack,
        itemStyle: s.itemStyle,
        emphasis: s.emphasis,
        datasetIndex: index + offset,
        lineStyle: s.lineStyle,
        markArea: this.markArea(s, this.numberOfForecastsValue),
        markLine: this.markLine(s)
      })
    })
  }

  seriesType () {
    return 'none'
  }

  options () {
    return merge(super.options(), {
      color: this.colors,
      xAxis: this.xAxisOptions(),
      yAxis: this.yAxisOptions(),
      dataset: this.transformToDataset(this.seriesValue),
      series: this.transformToSeries(this.seriesValue, {
        type: this.seriesType()
      }),
      grid: {
        left: 2,
        top: this.titleValue !== '' ? 40 : 10,
        bottom: this.gridBottomValue(),
        right: this.legendTypeValue === 'vertical' ? 160 : 30,
        containLabel: true
      }
    })
  }

  gridBottomValue () {
    switch (this.legendTypeValue) {
      case 'horizontal':
        return this.xAxisShowValue ? 30 : 15
      case 'horizontal_two_lines':
        return this.xAxisShowValue ? 60 : 30
      default:
        return 0
    }
  }

  tooltipOptions () {
    let type
    switch (this.tooltipValueFormatterValue) {
      case 'yAxis':
        type = this.yAxisTypeValue
        break
      case 'xAxis':
        type = this.xAxisTypeValue
        break
      default:
        type = this.tooltipValueFormatterValue
    }

    return merge(super.tooltipOptions(), {
      trigger: 'axis',
      axisPointer: tooltipAxisTypes(this.localeValue, this.tooltipAxisTypeValue),
      valueFormatter: tooltipValueFormatter(this.localeValue, type)
    })
  }

  tooltipFormatters () {
    return merge(super.tooltipFormatters(), {
      markLine: (params) => this.tooltipMarkLineFormatter(params)
    })
  }

  tooltipMarkLineFormatter (params) {
    const markLine = this.options()?.series[0]?.markLine
    const header = this.tooltipHeaderFormatter()(params)
    let markLineHtml = ''

    if (markLine?.data) {
      const formatter = this.tooltipOptions().valueFormatter
      const data = markLine.data[0]
      markLineHtml = `
        <div style="margin: 10px 0 0;line-height:1;">
          <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${markLine.lineStyle.color};"></span>
          <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">${data.name}</span>
          <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">${formatter(data.xAxis || data.yAxis)}</span>
          <div style="clear:both"></div>
        </div>
        <hr style="margin: 10px 0 0;line-height:1;"/>
      `
    }

    return `
      <div style="margin: 0px 0 0;line-height:1;">
        ${header}
        <div style="margin: ${header === '' ? '-10px' : '0px'} 0 0;line-height:1;">
          ${markLineHtml}
          ${params.map((series) => this.tooltipSeriesFormatter()(series)).join('\n')}
        </div>
        <div style="clear:both"></div>
      </div>
    `
  }

  xAxisOptions () {
    return merge({
      show: this.xAxisShowValue,
      name: this.xAxisNameValue,
      nameLocation: 'middle',
      nameGap: 30,
      scale: true,
      min: this.xAxisStartZeroValue ? 0 : null,
      axisLabel: cloneDeep(defaultAxisLabel),
      axisLine: cloneDeep(defaultAxisLine),
      axisTick: cloneDeep(defaultXAxisTick)
    }, this.axisTypes()[this.xAxisTypeValue])
  }

  yAxisOptions () {
    return merge({
      show: this.yAxisShowValue,
      name: this.yAxisNameValue,
      scale: true,
      min: this.yAxisStartZeroValue ? 0 : null,
      nameLocation: 'end',
      nameTextStyle: defaultBoldTextStyle,
      axisLabel: cloneDeep(defaultAxisLabel),
      axisLine: cloneDeep(defaultAxisLine)
    }, this.axisTypes()[this.yAxisTypeValue])
  }

  axisTypes () {
    return {
      area: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToArea(value, this.localeValue, { maximumFractionDigits: 0 }) }
      },
      value: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToHuman(value, this.localeValue) }
      },
      days: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToDays(value, this.localeValue, { maximumFractionDigits: 0 }) }
      },
      currency: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToCurrency(value, this.localeValue, { maximumFractionDigits: 0 }) }
      },
      currency_by_area: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToCurrencyPerArea(value, this.localeValue, { maximumFractionDigits: 1 }) }
      },
      percentage: {
        type: 'value',
        axisLabel: { formatter: (value) => numberToPercentage(value, this.localeValue, { maximumFractionDigits: 0 }) }
      },
      datetime: {
        type: 'time',
        minInterval: 86400000
      },
      date: {
        type: 'time',
        axisLabel: { formatter: '{dd}.{MM}.{yyyy}' },
        minInterval: 86400000
      },
      monthly: {
        type: 'time',
        axisLabel: { formatter: '{MMM} {yyyy}' },
        minInterval: 86400000
      },
      quarterly: {
        type: 'time',
        axisLabel: { formatter: 'Q{Q}\' {yyyy}' },
        minInterval: 86400000
      },
      yearly: {
        type: 'time',
        axisLabel: { formatter: '{yyyy}' },
        minInterval: 86400000
      },
      category: {
        type: 'category'
      },
      none: {
        type: 'value',
        axisLabel: { formatter: (_value) => '' }
      }
    }
  }

  markArea (s, numberOfForecasts) {
    if (!numberOfForecasts || !s.data.length) return

    return {
      silent: true,
      data: [[
        { xAxis: s.data.at(-1 * numberOfForecasts).x },
        { xAxis: s.data.at(-1).x }
      ]],
      itemStyle: {
        color: colors.base[300]
      }
    }
  }

  markLine (s) {
    if (!this.markLineValue || !s.data.length) return

    return merge(
      {
        silent: true,
        label: {
          position: 'insideEndBottom',
          formatter: ({ data }) => {
            const formatter = this.tooltipOptions().valueFormatter
            return `${data.name}: ${formatter(data.value)}`
          }
        },
        symbol: ['none', 'none'],
        lineStyle: {
          type: 'solid',
          color: colors.error,
          width: 2,
          cap: 'round'
        }
      }, this.markLineValue)
  }
}

function tooltipAxisTypes (locale, type) {
  return {
    date: {
      label: {
        formatter: ({ value }) => {
          return new Date(value).toLocaleDateString(locale)
        }
      }
    },
    monthly: {
      label: {
        formatter: ({ value }) => {
          const date = new Date(value)
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`
        }
      }
    },
    quarterly: {
      label: {
        formatter: ({ value }) => {
          const date = new Date(value)
          const quarter = Math.floor(date.getMonth() / 3) + 1
          return `Q${quarter}' ${date.getFullYear()}`
        }
      }
    },
    yearly: {
      label: {
        formatter: ({ value }) => {
          const date = new Date(value)
          return `${date.getFullYear()}`
        }
      }
    }
  }[type]
}

const defaultAxisLabel = {
  fontSize: 14,
  margin: 12,
  showMinLabel: true,
  showMaxLabel: true,
  hideOverlap: true
}

const defaultLineStyle = {
  color: colors.gray.inactive
}

const defaultAxisLine = {
  show: true,
  lineStyle: defaultLineStyle
}

const defaultXAxisTick = {
  inside: true,
  length: 5,
  lineStyle: {
    shadowColor: colors.gray.inactive,
    shadowOffsetY: 5,
    shadowOffsetX: 0,
    shadowBlur: 0
  }
}

export default {
  XYChartController
}
